// Firebase sign-in for the web client. The config comes from the server (GET /api/auth/config), so a
// deployment enables Firebase without rebuilding the client. The SDK is loaded only when it's used.
import { useQuery } from '@tanstack/react-query';
import type { SignupWorkspace } from '@mailpilot/shared';
import { api, type ResOf } from './api';
import { deviceKind, deviceName } from './desktop';

export type FirebaseProviderId = 'google' | 'microsoft' | 'apple' | 'github';

export const PROVIDER_LABEL: Record<FirebaseProviderId, string> = {
  google: 'Google',
  microsoft: 'Microsoft',
  apple: 'Apple',
  github: 'GitHub',
};

export function useAuthConfig() {
  return useQuery({ queryKey: ['auth-config'], queryFn: () => api('GET /api/auth/config'), staleTime: 5 * 60_000, retry: 1 });
}

type AuthModule = typeof import('firebase/auth');
let loaded: Promise<{ auth: ReturnType<AuthModule['getAuth']>; mod: AuthModule }> | null = null;

async function firebaseAuth(web: Record<string, string>) {
  if (!loaded) {
    loaded = (async () => {
      const [{ initializeApp, getApps }, mod] = await Promise.all([import('firebase/app'), import('firebase/auth')]);
      const app = getApps().find((a) => a.name === 'mailpilot') ?? initializeApp(web, 'mailpilot');
      const auth = mod.getAuth(app);
      // MailPilot keeps its own session; Firebase is only used to prove who you are.
      await mod.setPersistence(auth, mod.inMemoryPersistence);
      return { auth, mod };
    })().catch((e) => {
      loaded = null;
      throw e;
    });
  }
  return loaded;
}

function providerFor(mod: AuthModule, id: FirebaseProviderId) {
  switch (id) {
    case 'google': {
      const p = new mod.GoogleAuthProvider();
      p.setCustomParameters({ prompt: 'select_account' });
      return p;
    }
    case 'microsoft':
      return new mod.OAuthProvider('microsoft.com');
    case 'apple': {
      const p = new mod.OAuthProvider('apple.com');
      p.addScope('email');
      p.addScope('name');
      return p;
    }
    case 'github': {
      const p = new mod.GithubAuthProvider();
      p.addScope('user:email');
      return p;
    }
  }
}

/** Friendlier messages for the Firebase errors people actually hit. Returns null when the popup was simply closed. */
function firebaseMessage(e: unknown): string | null {
  const code = (e as { code?: string })?.code ?? '';
  if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') return null;
  if (code === 'auth/popup-blocked') return 'Your browser blocked the sign-in window. Allow pop-ups for MailPilot and try again.';
  if (code === 'auth/account-exists-with-different-credential') return 'You already use this email with a different sign-in method. Use that one.';
  if (code === 'auth/unauthorized-domain') return `This address (${window.location.hostname}) isn't an authorised domain in your Firebase project yet (Authentication → Settings → Authorised domains).`;
  if (code === 'auth/operation-not-allowed') return 'This sign-in method is not enabled in the Firebase project.';
  if (code === 'auth/network-request-failed') return "Couldn't reach the sign-in service. Check your connection.";
  return (e as Error)?.message || 'Sign-in failed.';
}

/** Get a fresh Firebase ID token through a provider's popup (null when the person closed it). */
export async function firebaseIdToken(web: Record<string, string>, provider: FirebaseProviderId): Promise<string | null> {
  const { auth, mod } = await firebaseAuth(web);
  try {
    const cred = await mod.signInWithPopup(auth, providerFor(mod, provider));
    const token = await cred.user.getIdToken(true);
    await mod.signOut(auth).catch(() => undefined);
    return token;
  } catch (e) {
    const msg = firebaseMessage(e);
    if (msg === null) return null;
    throw new Error(msg);
  }
}

/** Sign in (or sign up) to MailPilot with a Firebase provider. */
export async function signInWithFirebase(
  web: Record<string, string>,
  provider: FirebaseProviderId,
  workspace?: SignupWorkspace,
): Promise<ResOf<'POST /api/auth/firebase'> | null> {
  const idToken = await firebaseIdToken(web, provider);
  if (!idToken) return null;
  return api('POST /api/auth/firebase', {
    body: { idToken, deviceName: await deviceName(), deviceKind: deviceKind(), timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, workspace },
  });
}

export const firebaseProviders = (providers: string[]): FirebaseProviderId[] =>
  providers.filter((p): p is FirebaseProviderId => p in PROVIDER_LABEL);
